import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, unref as _unref } from "vue"

const _hoisted_1 = { class: "app-list-select-dropdown" }
const _hoisted_2 = { class: "app-list-select-dropdown__search" }
const _hoisted_3 = {
  key: 0,
  class: "app-list-select-dropdown__close"
}
const _hoisted_4 = { class: "app-list-select-dropdown__header" }
const _hoisted_5 = { class: "app-list-select-dropdown__header-title" }
const _hoisted_6 = ["onClick", "innerHTML"]
const _hoisted_7 = {
  key: 2,
  class: "app-list-select-dropdown__body-empty"
}
const _hoisted_8 = { class: "app-list-select-dropdown__body-item-text" }

import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { debounce } from "lodash";
import { highlightSearchText } from "@/utils/textHelpers";
import { AnalyticsApi } from "@/ api/AnalyticsApi";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

type AnalyticsList = {
	Id: number;
	Name: string;
	Position: number;
};

const take = 60;

export default /*@__PURE__*/_defineComponent({
  __name: 'AppListSelectDropdown',
  props: {
    segment: {},
    elementId: {},
    addedLists: {},
    showCloseIcon: { type: Boolean }
  },
  emits: ["close", "addList"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $input = ref<typeof AppInput | null>(null);
const $body = ref<HTMLDivElement | null>(null);

const isLoading = ref(true);
const filter = ref("");
const skip = ref(0);
const canLoadNext = ref(true);
const isInit = ref(false);
const lists = ref<AnalyticsList[] | null>(null);
const existLists = computed(() => props.addedLists ?? []);

const filteredLists = computed(() => {
	if (!lists.value) return [];

	let result: AnalyticsList[] = [];

	if (existLists.value.length) {
		const sorted = [...existLists.value] as AnalyticsList[];
		lists.value.forEach((item) => {
			if (!existLists.value.find((c) => c.Id === item.Id)) {
				sorted.push(item);
			}
		});
		result = sorted;
	} else {
		result = [...lists.value];
	}

	result.sort((a, b) =>
		a.Name.localeCompare(b.Name, "ru", { sensitivity: "base" }),
	);

	if (!filter.value) return result;
	const search = filter.value.toLowerCase();
	return result.filter((item) => item.Name.toLowerCase().includes(search));
});

const loaderStyle = computed(() => ({
	"background-color": "rgba(255, 255, 255, 0.5)",
}));


const enter = () => {
	if (
		filter.value &&
		filteredLists.value.length !== 0 &&
		!existLists.value.find((l) => l.Id === filteredLists.value[0].Id)
	) {
		onChangeListStage(true, filteredLists.value[0].Id);
	}
};

const onFetchLists = async () => {
	try {
		if (canLoadNext.value) {
			isLoading.value = true;
			const listsArray = await AnalyticsApi.getLists(
				filter.value,
				0,
				props.segment,
				props.elementId,
			);
			lists.value =
				skip.value === 0
					? listsArray.Lists
					: lists.value
					? [...lists.value, ...listsArray.Lists]
					: listsArray.Lists;
			skip.value += take;
		}
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const onSearch = async () => {
	$body.value?.scrollTo({ top: 0 });
	canLoadNext.value = true;
	skip.value = 0;
	await onFetchLists();
};

const debouncedSearch = debounce(onSearch, 500);

const onAddElementToList = async (listId: number) => {
	try {
		isLoading.value = true;
		await AnalyticsApi.addElementToList(listId, "", props.segment, props.elementId);
		emit("addList");
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const onAddElementToNewList = async () => {
	try {
		isLoading.value = true;
		await AnalyticsApi.addElementToList(undefined, filter.value, props.segment, props.elementId);
		emit("addList");
		await onFetchLists();
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const onDeleteFromList = async (listId: number) => {
	try {
		isLoading.value = true;
		await AnalyticsApi.removeElementIntoList(listId, props.segment, props.elementId);
		emit("addList");
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const dropAllLists = async () => {
	try {
		isLoading.value = true;
		await Promise.allSettled(
			existLists.value.map((l) =>
				AnalyticsApi.removeElementIntoList(l.Id, props.segment, props.elementId),
			),
		);
		emit("addList");
	} catch (e) {
		console.error(e);
	} finally {
		isLoading.value = false;
	}
};

const isListAdded = (listId: number) =>
	!!existLists.value.find((c) => c.Id === listId);

const onChangeListStage = async (stage: boolean, listId: number) => {
	if (!lists.value) return;
	if (stage) {
		await onAddElementToList(listId);
	} else {
		await onDeleteFromList(listId);
	}
};

const onToggleByName = async (listId: number) => {
	await onChangeListStage(!isListAdded(listId), listId);
};

watch(filter, (newVal, oldVal) => {
	if (newVal !== oldVal) debouncedSearch();
});

watch(isInit, (newValue) => {
	if (newValue) {
		nextTick(() => {
			$body.value?.addEventListener("scroll", async () => {
				if (canLoadNext.value && !isLoading.value) {
					if (
						($body.value?.scrollTop ?? 0) + ($body.value?.offsetHeight ?? 0) >
						($body.value?.scrollHeight ?? 0) - 500
					) {
						await onFetchLists();
					}
				}
			});
		});
	}
});

onMounted(async () => {
	await onFetchLists();
	isInit.value = true;
	await nextTick(() => $input.value?.focusInput?.());
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(AppLoader, {
          key: 0,
          style: _normalizeStyle(loaderStyle.value)
        }, null, 8, ["style"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(AppInput, {
        ref_key: "$input",
        ref: $input,
        modelValue: filter.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filter).value = $event)),
        class: "app-list-select-dropdown__search-input",
        placeholder: _ctx.$t('search_short'),
        "show-add-button": "",
        "add-hint-text": _ctx.$t('create_and_add_list'),
        onAdd: onAddElementToNewList,
        onPressEnter: enter
      }, null, 8, ["modelValue", "placeholder", "add-hint-text"]),
      (props.showCloseIcon)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(AppTooltip, {
              text: _ctx.$t('close_esc_label')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  name: "close",
                  width: "20px",
                  color: "#D2D2D2",
                  "hover-color": "#499259",
                  cursor: "pointer",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
                })
              ]),
              _: 1
            }, 8, ["text"])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t("available_lists")), 1),
      _createElementVNode("div", {
        class: "app-list-select-dropdown__header-drop-btn",
        onClick: dropAllLists
      }, _toDisplayString(_ctx.$t("selector_reset")), 1)
    ]),
    (filteredLists.value?.length)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "app-list-select-dropdown__body",
          ref_key: "$body",
          ref: $body
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredLists.value, (element) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "app-list-select-dropdown__body-item",
              key: element.Id
            }, [
              _createVNode(AppCheckbox, {
                style: { height: '16px', marginRight: '8px' },
                value: isListAdded(element.Id),
                text: "",
                onInput: ($event: any) => (onChangeListStage($event, element.Id))
              }, null, 8, ["value", "onInput"]),
              _createElementVNode("div", {
                class: "app-list-select-dropdown__body-item-text",
                onClick: ($event: any) => (onToggleByName(element.Id)),
                innerHTML: _unref(highlightSearchText)(filter.value, element.Name)
              }, null, 8, _hoisted_6)
            ]))
          }), 128))
        ], 512))
      : (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, _toDisplayString(_ctx.$t("list_not_found")), 1)
        ]))
  ]))
}
}

})